import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";

/**
 * This function calls a provided "request function" and returns the results.
 *
 *   const [strain] = useRequest(getStrain, [slug]);
 *
 * The hook also returns a ref that can be used to conditionally call the
 * request function when the element that the ref is attached to is within
 * the viewport. This behavior is automatically activated when the ref is
 * used; if the ref is not used, the request will be called immediately
 * on mount.
 *
 *   const [strain, { inViewRef }] = useRequest(getStrain, [slug]);
 *
 *   <div ref={inViewRef} />
 *
 */
function useRequest<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- using any here allows the type of the arguments to be inferred
  T extends (...args: any[]) => Promise<Returns>,
  Returns = Awaited<ReturnType<T>>
>(
  request: T,
  args: Parameters<T>
): [Returns | null, { inViewRef: ReturnType<typeof useInView>[0] }] {
  /**
   * This ref tracks whether we should avoid making this API call until some
   * element on the page is visible.
   */
  const loadOnScroll = useRef(false);

  const [ref, inView] = useInView({
    triggerOnce: true,
  });

  /**
   * While this isn't an "event", using the `useCallback` hook here prevents
   * the identity of this wrapper ref from changing, like a normal ref would.
   */
  const inViewRef: typeof ref = useCallback((...args) => {
    /**
     * If the ref is used, then we can assume that we shouldn't make the API
     * request until the attached element is in view and so mark this as true.
     */
    loadOnScroll.current = true;
    ref(...args);
  }, []);

  const [data, setData] = useState<Returns | null>(null);

  useEffect(() => {
    if (inView || !loadOnScroll.current) {
      request(...args).then(setData);
    }
  }, [JSON.stringify(args), inView]);

  return [
    data,
    {
      inViewRef,
    },
  ];
}

export default useRequest;
